import {
    Contact,
    ContactInfo,
    ContactHistory,
    ContactPattern,
    ContactsGrowth,
    ContactsOverview,
    AddContactPayload,
} from '@dev/ee-api/ee-api-models';
import { AxiosError } from 'axios';
import { createCachedSelector } from 're-reselect';
import { isNil } from 'lodash-es';
import { ContactsUnsubscribedParams, ExportContactsUnsubscribedParams } from '@/services/ElasticApi';
import * as Types from './types';
import { ShowDialogParams } from '../dialog/types';
import { ApplicationState } from '..';
import { getListstStore } from '../lists/actions';
import { getSegmentsStore } from '../segments/actions';

const { ContactsActionTypes } = Types;

export const AddSingleContact = (
    params: Types.PostContactsParams,
    redirectPath?: string
): Types.AddSingleContactAction => ({
    type: ContactsActionTypes.CREATE_SINGLE,
    payload: {
        ...params,
    },
    redirectPath,
});

export const AddMultipleContacts = (params: Types.PostContactsParams): Types.AddMultipleContactsAction => ({
    type: ContactsActionTypes.CREATE_MULTIPLE,
    payload: {
        ...params,
    },
    meta: { thunk: true },
});

export const PostContactsStart = (params: Types.PostContactsParams): Types.PostContactsFetchStartAction => ({
    type: ContactsActionTypes.CREATE_START,
    payload: {
        ...params,
    },
    meta: { thunk: true },
});

export const PostContactsError = (meta: Record<string, unknown>): Types.PostContactsFetchErrorAction => ({
    type: ContactsActionTypes.CREATE_ERROR,
    meta,
    error: true,
});

export const PostContactsSuccess = (
    validationStatus: Types.ValidationInterface,
    meta: Record<string, unknown>
): Types.PostContactsFetchSuccessAction => ({
    type: ContactsActionTypes.CREATE_SUCCESS,
    payload: {
        validationStatus,
    },
    meta,
});

export const GetContactsPatternStart = (params: {
    fileName: string;
    encodingName: string;
    errorModal?: ShowDialogParams;
}): Types.GetContactsPatternFetchStartAction => ({
    type: ContactsActionTypes.FETCH_PATTERN_START,
    payload: {
        fileName: params.fileName,
        encodingName: params.encodingName,
        errorModal: params.errorModal,
    },
});

export const GetContactsPatternError = (): Types.GetContactsPatternFetchErrorAction => ({
    type: ContactsActionTypes.FETCH_PATTERN_ERROR,
});

export const GetContactsPatternSuccess = (
    contactPattern: ContactPattern
): Types.GetContactsPatternFetchSuccessAction => ({
    type: ContactsActionTypes.FETCH_PATTERN_SUCCESS,
    payload: {
        contactPattern,
    },
});

export const PostContactsWithPatternStart = (
    params: Types.PostWithPatternParams
): Types.PostContactsWithPatternFetchStartAction => ({
    type: ContactsActionTypes.CREATE_WITH_PATTERN_START,
    payload: {
        ...params,
    },
    meta: {
        thunk: true,
    },
});

export const PostContactsWithPatternError = (
    meta: Record<string, unknown>
): Types.PostContactsWithPatternFetchErrorAction => ({
    type: ContactsActionTypes.CREATE_WITH_PATTERN_ERROR,
    meta,
    error: true,
});

export const PostContactsWithPatternSuccess = (
    validationStatus: Types.ValidationInterface,
    contacts: number,
    meta: Record<string, unknown>
): Types.PostContactsWithPatternFetchSuccessAction => ({
    type: ContactsActionTypes.CREATE_WITH_PATTERN_SUCCESS,
    payload: {
        validationStatus,
        contacts,
        isUploading: false,
    },
    meta,
});

export const SetPostParams = (postContactsParams: Types.PostContactsParams): Types.SetAddContactParams => ({
    type: ContactsActionTypes.SET_POST_PARAMS,
    payload: {
        postContactsParams,
    },
});

export const PostParamsStart = (postContactsParams: Types.PostContactsParams): Types.SetAddContactParams => ({
    type: ContactsActionTypes.SET_POST_START,
    payload: {
        postContactsParams,
    },
});

export const ContactsOverviewStartLoading = (params: {
    From: string;
    To: string;
}): Types.ContactsOverviewFetchStartAction => ({
    type: ContactsActionTypes.FETCH_OVERVIEW_START,
    payload: {
        from: params.From,
        to: params.To,
    },
});

export const ContactsOverviewLoadingError = (): Types.ContactsOverviewFetchErrorAction => ({
    type: ContactsActionTypes.FETCH_OVERVIEW_ERROR,
});

export const ContactsOverviewLoadingSuccess = (
    contactsOverview: ContactsOverview
): Types.ContactsOverviewFetchSuccessAction => ({
    type: ContactsActionTypes.FETCH_OVERVIEW_SUCCESS,
    payload: {
        contactsOverview,
    },
});

export const ContactsStartLoading = (params: Types.GetContactsInterface): Types.ContactsFetchStartAction => ({
    type: ContactsActionTypes.FETCH_START,
    payload: {
        params,
        isContactsLoading: true,
    },
});

export const ContactsLoadingError = (collection: Contact[]): Types.ContactsFetchErrorAction => ({
    type: ContactsActionTypes.FETCH_ERROR,
    payload: {
        isContactsLoading: false,
        collection,
    },
});

export const ContactsLoadingSuccess = (
    collection: ContactInfo[],
    counts: Types.ContactCounts
): Types.ContactsFetchSuccessAction => ({
    type: ContactsActionTypes.FETCH_SUCCESS,
    payload: {
        collection,
        counts,
        isContactsLoading: false,
    },
});

export const SingleContactStartLoading = (
    email: string,
    showErrorToast = false
): Types.SingleContactFetchStartAction => ({
    type: ContactsActionTypes.FETCH_CONTACT_START,
    payload: {
        email,
        isContactsLoading: true,
        showErrorToast,
    },
    meta: {
        thunk: true,
    },
});

export const SingleContactLoadingError = (
    meta: Record<string, unknown>,
    error: AxiosError | Error
): Types.SingleContactFetchErrorAction => ({
    type: ContactsActionTypes.FETCH_CONTACT_ERROR,
    payload: {
        error,
        isContactsLoading: false,
    },
    meta,
    error: true,
});

export const SingleContactLoadingSuccess = (
    selectedContact: ContactInfo,
    meta: Record<string, unknown>
): Types.SingleContactFetchSuccessAction => ({
    type: ContactsActionTypes.FETCH_CONTACT_SUCCESS,
    payload: {
        selectedContact,
        isContactsLoading: false,
    },
    meta,
});

export const ContactsDeleteStart = (params: {
    deleteParams: Types.ContactsDeleteParams;
    loadParams?: Types.ContactsLoadParams;
}): Types.ContactsDeleteStartAction => ({
    type: ContactsActionTypes.DELETE_START,
    payload: {
        params,
    },
});

export const ContactsDeleteError = (): Types.ContactsDeleteErrorAction => ({
    type: ContactsActionTypes.DELETE_ERROR,
});

export const ContactsDeleteSuccess = (collection): Types.ContactsDeleteSuccessAction => ({
    type: ContactsActionTypes.DELETE_SUCCESS,
    payload: {
        collection,
    },
});

export const ContactsBulkUpdateStart = (params: Types.ContactsBulkUpdate): Types.ContactsBulkUpdateStartAction => ({
    type: ContactsActionTypes.BULK_UPDATE_START,
    payload: {
        params,
    },
    meta: {
        thunk: true,
    },
});

export const ContactsBulkUpdateError = (meta: Record<string, any>): Types.ContactsBulkUpdateErrorAction => ({
    type: ContactsActionTypes.BULK_UPDATE_ERROR,
    error: true,
    meta,
});

export const ContactsBulkUpdateSuccess = (meta: Record<string, any>): Types.ContactsBulkUpdateSuccessAction => ({
    type: ContactsActionTypes.BULK_UPDATE_SUCCESS,
    meta,
});

export const ContactUpdateStart = (params: Types.ContactFieldsInterface): Types.ContactUpdateStartAction => ({
    type: ContactsActionTypes.UPDATE_START,
    payload: {
        params,
        isContactUpdating: true,
    },
});

export const ContactUpdateError = (): Types.ContactUpdateErrorAction => ({
    type: ContactsActionTypes.UPDATE_ERROR,
    payload: {
        isContactUpdating: false,
    },
});

export const ContactUpdateSuccess = (selectedContact: ContactInfo): Types.ContactUpdateSuccessAction => ({
    type: ContactsActionTypes.UPDATE_SUCCESS,
    payload: {
        selectedContact,
        isContactUpdating: false,
    },
});

export const ContactDeselect = (): Types.DeselectContactAction => ({
    type: ContactsActionTypes.DESELECT,
});

export const ContactGetHistoryStart = (
    params: Types.SingleContactPaginationInterface
): Types.ContactGetHistoryStartAction => ({
    type: ContactsActionTypes.GET_HISTORY_START,
    payload: {
        params,
        isContactHistoryLoading: true,
    },
});

export const ContactGetHistoryError = (): Types.ContactGetHistoryErrorAction => ({
    type: ContactsActionTypes.GET_HISTORY_ERROR,
    payload: {
        isContactHistoryLoading: false,
    },
});

export const ContactGetHistorySuccess = (
    contactHistory: ContactHistory[],
    contactHistoryCounts: number
): Types.ContactGetHistorySuccessAction => ({
    type: ContactsActionTypes.GET_HISTORY_SUCCESS,
    payload: {
        contactHistory,
        contactHistoryCounts,
        isContactHistoryLoading: false,
    },
});

export const ContactGetGrowthStart = (from: string, to: string, isUrlUpdateDisabled?: boolean): Types.ContactsGetGrowthStartAction => ({
    type: ContactsActionTypes.GET_GROWTH_START,
    payload: {
        from,
        to,
        isContactsGrowthLoading: true,
        isUrlUpdateDisabled,
    },
});

export const ContactGetGrowthError = (): Types.ContactsGetGrowthErrorAction => ({
    type: ContactsActionTypes.GET_GROWTH_ERROR,
    payload: {
        isContactsGrowthLoading: false,
    },
});

export const ContactGetGrowthSuccess = (
    contactsGrowth: Types.ContactGrowth[]
): Types.ContactsGetGrowthSuccessAction => ({
    type: ContactsActionTypes.GET_GROWTH_SUCCESS,
    payload: {
        contactsGrowth,
        isContactsGrowthLoading: false,
    },
});

export const ClearUploadData = (): Types.ClearUploadDataAction => ({
    type: ContactsActionTypes.CLEAR_UPLOAD_DATA,
});

export const ClearContactsAndCounts = (): Types.ClearContactsAndCountsAction => ({
    type: ContactsActionTypes.CLEAR_CONTACTS_AND_COUNTS,
});

export const GetContactsUnsubscribedOverview = (
    from: string,
    to: string
): Types.GetContactsUnsubscribedOverviewAction => ({
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW,
    payload: {
        from,
        to,
    },
});
export const GetContactsUnsubscribedOverviewSuccess = (
    unsubscribedContacts: Types.UnsubscribedContactsInterface
): Types.GetContactsUnsubscribedOverviewSuccessAction => ({
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS,
    payload: unsubscribedContacts,
});
export const GetContactsUnsubscribedOverviewError = (): Types.GetContactsUnsubscribedOverviewErrorAction => ({
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR,
});

export const GetContactsUnsubscribed = (params: ContactsUnsubscribedParams): Types.GetContactsUnsubscribedAction => ({
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED,
    payload: {
        ...params,
    },
});
export const GetContactsUnsubscribedSuccess = (
    unsubscribedContacts: Types.UnsubscribedContactsInterface
): Types.GetContactsUnsubscribedSuccessAction => ({
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_SUCCESS,
    payload: unsubscribedContacts,
});
export const GetContactsUnsubscribedError = (): Types.GetContactsUnsubscribedErrorAction => ({
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_ERROR,
});

export const ExportContactsUnsubscribed = (
    params: ExportContactsUnsubscribedParams
): Types.ExportContactsUnsubscribedAction => ({
    type: ContactsActionTypes.EXPORT_CONTACTS_UNSUBSCRIBED,
    payload: params,
});

export const SetPickedContactEmail = (pickedContactEmail?: string): Types.SetPickedContactEmailAction => ({
    type: ContactsActionTypes.SET_PICKED_CONTACT_EMAIL,
    payload: {
        pickedContactEmail,
    },
});

export const StartReshardContacts = (): Types.StartReshardContactsAction => ({
    type: ContactsActionTypes.RESHARD_CONTACTS_START,
    meta: {
        thunk: true,
    },
});

export const StartPollingReshardStatus = (): Types.CheckReshardContactsStatusAction => ({
    type: ContactsActionTypes.RESHARD_CONTACTS_STATUS_START_LONG_POLLING,
    meta: {
        thunk: true,
    },
});

export const CancelReshardStatusPolling = (
    meta?: Record<string, unknown>
): Types.CancelReshardContactsStatusPollingAction => ({
    type: ContactsActionTypes.CANCEL_RESHARD_STATUS_LONG_POLLING,
    meta,
});

export const getContactsStore = (state: ApplicationState) => state.contacts;
export const getContactsOverviewStore = (state: ApplicationState): ContactsOverview => state.contacts.contactsOverview;
export const getContactsGrowthStore = (state: ApplicationState): ContactsGrowth[] => state.contacts.contactsGrowth;
export const getUnsubscribeContactsStore = (state: ApplicationState): Types.UnsubscribedContactsInterface =>
    state.contacts.unsubscribedContacts;
export const getIsCreatingContactsStore = (state: ApplicationState): boolean => state.contacts.isCreatingContacts;
export const getContactsCounts = (state: ApplicationState): Types.ContactCounts =>
    state?.contacts?.counts || ({} as Types.ContactCounts);
export const getContactsCollection = (state: ApplicationState) => state?.contacts?.collection;
export const getIsContactsLoading = (state: ApplicationState) => state?.contacts?.isContactsLoading;
export const getIsContactsUploading = (state: ApplicationState) => state?.contacts?.isUploading;

export const checkIfDataIsLoadedForContactsList = createCachedSelector(
    (store, _displayName) => getContactsStore(store),
    getListstStore,
    getSegmentsStore,
    (contacts, lists, segments) => ({
        isDataLoaded:
            !contacts?.isContactsLoading &&
            !isNil(contacts?.collection) &&
            !lists?.isListLoading &&
            !segments?.isLoadingSegments,
        collection: contacts?.collection,
    })
)((_state_, displayName) => displayName);

export const getDataForContactListTable = createCachedSelector(
    (state, _displayName) => getContactsStore(state),
    (state, _displayName) => getListstStore(state),
    (contacts, lists) => ({
        areAllListNamesLoaded: !!lists?.listNamesCollection,
        hasMoreContacts: contacts?.hasMoreContacts,
        isContactsLoading: contacts?.isContactsLoading,
    })
)((_state_, displayName) => _state_.lists?.selected?.ListName || _state_?.segments?.selected?.Name || displayName);
